// ============================================================
// ByOurConsent.com -- Forum Page
// /forum  -- list of discussion threads
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api    from '../utils/api';
import toast  from 'react-hot-toast';

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

const fmtRelative = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return fmtDate(d);
};

const ForumPage = () => {
    const { user, isAdmin } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [threads,    setThreads]    = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [showForm,   setShowForm]   = useState(false);
    const [form,       setForm]       = useState({ title: '', body: '', category_id: '' });
    const [saving,     setSaving]     = useState(false);

    const activeCategory = searchParams.get('category') || '';
    const isLeader = user?.role === 'group_leader';
    const canPost  = isAdmin || isLeader;

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ limit: 20 });
        if (activeCategory) params.set('category', activeCategory);
        api.get(`/forum/threads?${params}`)
            .then(r => setThreads(r.data.threads || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [activeCategory]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get('/articles/categories')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => {});
    }, []);

    const handlePost = async () => {
        if (!form.title) { toast.error('Title required'); return; }
        setSaving(true);
        try {
            const r = await api.post('/forum/threads', form);
            toast.success('Discussion started!');
            setShowForm(false);
            setForm({ title: '', body: '', category_id: '' });
            load();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to post'); }
        finally { setSaving(false); }
    };

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'9px 12px', fontSize:'14px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box' };
    const lStyle = { display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'5px' };

    return (
        <>
            <Helmet>
                <title>Forum — By Our Consent</title>
            </Helmet>

            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '2rem 1.5rem' }}>
                <div style={{ maxWidth: '860px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '8px' }}>
                            Community
                        </div>
                        <h1 style={{ fontSize: '34px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.15, marginBottom: '6px' }}>
                            Discussion forum
                        </h1>
                        <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                            Civic conversations started by our editors and chapter leaders.
                        </p>
                    </div>
                    {canPost && (
                        <button onClick={() => setShowForm(!showForm)}
                            style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px', flexShrink: 0 }}>
                            {showForm ? 'Cancel' : '+ Start discussion'}
                        </button>
                    )}
                </div>
            </div>

            <div style={{ maxWidth: '860px', margin: '0 auto', padding: '1.5rem' }}>

                {/* New thread form */}
                {showForm && canPost && (
                    <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                            Start a new discussion
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={lStyle}>Title *</label>
                            <input style={iStyle} value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Discussion topic..." />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={lStyle}>Category</label>
                            <select style={iStyle} value={form.category_id} onChange={e => setForm(p => ({...p, category_id: e.target.value}))}>
                                <option value="">No category</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={lStyle}>Opening post</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={5} value={form.body} onChange={e => setForm(p => ({...p, body: e.target.value}))} placeholder="Start the conversation..." />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowForm(false)}
                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '8px 16px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                Cancel
                            </button>
                            <button onClick={handlePost} disabled={saving}
                                style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '3px', opacity: saving ? 0.7 : 1 }}>
                                {saving ? 'Posting...' : 'Start discussion →'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Category filter */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <button onClick={() => setSearchParams({})}
                        style={{ background: !activeCategory ? '#1e2a3a' : 'transparent', color: !activeCategory ? '#e8dfc8' : '#4a5060', border: `0.5px solid ${!activeCategory ? '#1e2a3a' : '#c8beaa'}`, padding: '4px 12px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '20px', cursor: 'pointer' }}>
                        All
                    </button>
                    {categories.map(c => (
                        <button key={c.slug} onClick={() => setSearchParams({ category: c.slug })}
                            style={{ background: activeCategory === c.slug ? '#1e2a3a' : 'transparent', color: activeCategory === c.slug ? '#e8dfc8' : '#4a5060', border: `0.5px solid ${activeCategory === c.slug ? '#1e2a3a' : '#c8beaa'}`, padding: '4px 12px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '20px', cursor: 'pointer' }}>
                            {c.name}
                        </button>
                    ))}
                </div>

                {/* Thread list */}
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
                ) : threads.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>💬</div>
                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>
                            No discussions yet
                        </div>
                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                            {canPost ? 'Start the first discussion!' : 'Check back soon.'}
                        </div>
                    </div>
                ) : (
                    threads.map((t, i) => (
                        <Link key={t.id} to={`/forum/${t.slug}`} style={{ textDecoration: 'none' }}>
                            <div style={{ background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-start', borderLeft: t.is_pinned ? '3px solid #8b6a3a' : '3px solid transparent' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#ede8dc'}
                                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#f5f0e8' : '#ede8dc'}>

                                {/* Avatar */}
                                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#1e2a3a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e8dfc8', fontSize: '15px', fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 600, flexShrink: 0 }}>
                                    {t.author_name?.[0] || '?'}
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '3px', flexWrap: 'wrap' }}>
                                        {t.is_pinned && <span style={{ fontSize: '9px', fontFamily: 'sans-serif', color: '#8b6a3a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>📌 Pinned</span>}
                                        {t.is_locked && <span style={{ fontSize: '9px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.1em', textTransform: 'uppercase' }}>🔒 Locked</span>}
                                        {t.category_name && (
                                            <span style={{ fontSize: '9px', background: '#ede8dc', border: '0.5px solid #c8beaa', padding: '1px 7px', borderRadius: '20px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                                {t.category_name}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                                        {t.title}
                                    </div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                        By {t.author_name} · {fmtDate(t.created_at)}
                                        {t.last_reply_at && ` · Last reply ${fmtRelative(t.last_reply_at)}`}
                                    </div>
                                </div>

                                {/* Stats */}
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                        {t.reply_count}
                                    </div>
                                    <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                        {t.reply_count === 1 ? 'reply' : 'replies'}
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))
                )}

                {!canPost && user && (
                    <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', textAlign: 'center' }}>
                        Discussions are started by our editorial team and chapter leaders. All members can reply.
                    </div>
                )}
                {!user && (
                    <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', textAlign: 'center' }}>
                        <Link to="/login" style={{ color: '#8b1a1a' }}>Log in</Link> to join the discussion.
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default ForumPage;
